import React, { useState, useRef, useEffect } from 'react';

interface Message {
    id: string;
    sender: 'user' | 'doctor';
    text: string;
    image?: string;
    time: string;
}

interface ChatInterfaceProps {
    onBack?: () => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ onBack }) => {
    const [messages, setMessages] = useState<Message[]>([
        { id: '1', sender: 'doctor', text: "Hello John! I see you're concerned about Max's paw. Can you describe the symptoms? Is he limping?", time: '10:30 AM' },
        { id: '2', sender: 'user', text: "Hi Dr. Sarah. Yes, he started limping this morning after our walk. He keeps licking his front right paw.", time: '10:32 AM' },
        { id: '3', sender: 'user', text: "", image: 'https://images.unsplash.com/photo-1535930891776-0c2dfb7daeda?auto=format&fit=crop&w=400&q=80', time: '10:33 AM' },
    ]);
    const [inputText, setInputText] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSendMessage = () => {
        if (!inputText.trim()) return;

        const newMessage: Message = {
            id: Date.now().toString(),
            sender: 'user',
            text: inputText,
            time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
        };

        setMessages(prev => [...prev, newMessage]);
        setInputText('');

        setTimeout(() => {
            const responses = [
                "I see. Let me check that photo. The paw does look a bit red. Has Max been eating normally?",
                "Thank you for sending the photo. Does the paw feel warm to the touch?",
                "I understand. I'd recommend bringing Max in for a check-up if the limping continues for more than 24 hours.",
                "That sounds concerning. Has Max shown any other symptoms like loss of appetite or lethargy?"
            ];
            const randomResponse = responses[Math.floor(Math.random() * responses.length)];

            const doctorResponse: Message = {
                id: (Date.now() + 1).toString(),
                sender: 'doctor',
                text: randomResponse,
                time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
            };
            setMessages(prev => [...prev, doctorResponse]);
        }, 1500);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <div className="flex h-full bg-background-light dark:bg-background-dark">
            <div className="w-80 bg-white dark:bg-surface-dark border-r border-gray-200 dark:border-gray-800 flex flex-col hidden md:flex">
                <div className="p-4 border-b border-gray-200 dark:border-gray-800">
                    <div className="relative">
                        <span className="material-icons-round absolute left-3 top-2.5 text-gray-400">search</span>
                        <input type="text" placeholder="Search consultations..." className="w-full bg-gray-100 dark:bg-background-dark rounded-xl pl-10 pr-4 py-2 text-sm border-none focus:ring-1 focus:ring-primary" />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                    <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase">Active</div>
                    <div className="p-2 space-y-1">
                        <div className="p-3 bg-primary/10 border border-primary/20 rounded-xl cursor-pointer">
                            <div className="flex justify-between items-start mb-1">
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                                    <span className="font-bold text-sm">Max (Dog)</span>
                                </div>
                                <span className="text-[10px] text-gray-500">10:42 AM</span>
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-300 truncate">Dr. Sarah: Let's check the paw...</p>
                        </div>
                    </div>
                    <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase mt-2">History</div>
                    <div className="p-2 space-y-1">
                        <div className="p-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl cursor-pointer opacity-70">
                            <div className="flex justify-between items-start mb-1">
                                <span className="font-bold text-sm">Luna (Cat)</span>
                                <span className="text-[10px] text-gray-500">Yesterday</span>
                            </div>
                            <p className="text-xs text-gray-500 truncate">Vaccination scheduled.</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 flex flex-col h-full relative">
                <header className="h-16 bg-white dark:bg-surface-dark border-b border-gray-200 dark:border-gray-800 flex items-center justify-between px-6 shrink-0">
                    <div className="flex items-center gap-3">
                        {onBack && (
                            <button onClick={onBack} className="md:hidden p-2 -ml-2 text-gray-400 hover:text-primary">
                                <span className="material-icons-round">arrow_back</span>
                            </button>
                        )}
                        <img src="https://i.pravatar.cc/150?u=a042581f4e29026704d" className="w-10 h-10 rounded-full ring-2 ring-primary/30" alt="Dr Sarah" />
                        <div>
                            <h2 className="font-bold text-sm flex items-center gap-2">Dr. Sarah Jenkins <span className="bg-primary/20 text-primary text-[10px] px-1.5 py-0.5 rounded font-bold">DVM</span></h2>
                            <p className="text-xs text-gray-500 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500"></span> Online</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button className="bg-primary hover:bg-primary-dark text-background-dark px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2 shadow-lg shadow-primary/20">
                            <span className="material-icons-round text-sm">videocam</span> Video Call
                        </button>
                        <button className="p-2 text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40"><span className="material-icons-round">warning</span></button>
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50 dark:bg-background-dark">
                    <div className="flex justify-center"><span className="text-xs text-gray-400 bg-gray-200 dark:bg-surface-dark px-3 py-1 rounded-full">Today</span></div>

                    {messages.map((msg) => (
                        <div key={msg.id} className={`flex gap-4 ${msg.sender === 'user' ? 'flex-row-reverse' : ''}`}>
                            {msg.sender === 'doctor' && (
                                <img src="https://i.pravatar.cc/150?u=a042581f4e29026704d" className="w-8 h-8 rounded-full self-end" alt="Dr" />
                            )}
                            {msg.sender === 'user' && (
                                <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-white text-xs font-bold self-end">JD</div>
                            )}
                            <div className={`${msg.sender === 'user' ? 'bg-primary text-background-dark' : 'bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-700'} rounded-2xl rounded-bl-none p-4 max-w-lg shadow-sm`}>
                                {msg.image && (
                                    <img src={msg.image} className="rounded-lg mb-2 w-64 h-48 object-cover" alt="Attachment" />
                                )}
                                {msg.text && <p className="text-sm font-medium">{msg.text}</p>}
                            </div>
                        </div>
                    ))}

                    <div className="flex gap-4">
                        <img src="https://i.pravatar.cc/150?u=a042581f4e29026704d" className="w-8 h-8 rounded-full self-end" alt="Dr" />
                        <div className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-700 rounded-2xl rounded-bl-none p-3 w-16 flex items-center gap-1">
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce delay-75"></div>
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce delay-150"></div>
                        </div>
                    </div>
                    <div ref={messagesEndRef} />
                </div>

                <div className="p-4 bg-white dark:bg-surface-dark border-t border-gray-200 dark:border-gray-800 shrink-0">
                    <div className="flex gap-3 items-end max-w-4xl mx-auto">
                        <button className="p-3 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-primary transition-colors"><span className="material-icons-round">add_photo_alternate</span></button>
                        <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center">
                            <textarea
                                rows={1}
                                placeholder="Type a message..."
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                onKeyPress={handleKeyPress}
                                className="w-full bg-transparent border-none focus:ring-0 text-sm p-3 resize-none max-h-32 outline-none"
                            />
                        </div>
                        <button
                            onClick={handleSendMessage}
                            disabled={!inputText.trim()}
                            className="p-3 rounded-xl bg-primary text-background-dark hover:bg-primary-dark transition-colors shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="material-icons-round">send</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatInterface;